import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ParsedRecord {
  seller_id: string;
  county: string;
  origin_state: string;
  recorded_date: string;
  document_type: string;
  pid: string;
  first_name: string;
  last_name: string;
  suffix: string;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  consideration: number | null;
}

interface Props {
  onImportComplete: () => void;
}

const parseName = (raw: string): { first_name: string; last_name: string; suffix: string } => {
  let cleaned = raw.trim().replace(/\n/g, ' ').replace(/\s+/g, ' ');
  
  // Extract suffix
  let suffix = '';
  const suffixMatch = cleaned.match(/[,/]\s*(JR\.?|SR\.?|II|III|IV)\s*$/i);
  if (suffixMatch) {
    suffix = suffixMatch[1].replace('.', '');
    cleaned = cleaned.replace(suffixMatch[0], '').trim();
  }

  // Handle "/ II" or "/ JR" style
  const slashSuffix = cleaned.match(/\/ \s*(JR\.?|SR\.?|II|III|IV)\s*$/i);
  if (slashSuffix) {
    suffix = slashSuffix[1].replace('.', '');
    cleaned = cleaned.replace(slashSuffix[0], '').trim();
  }

  // LAST, FIRST MIDDLE format
  const parts = cleaned.split(',').map(p => p.trim());
  if (parts.length >= 2) {
    const lastName = parts[0];
    // Take first name only (drop middle)
    const firstParts = parts[1].split(/\s+/);
    const firstName = firstParts[0];
    return { first_name: toProperCase(firstName), last_name: toProperCase(lastName), suffix };
  }

  // Fallback single word
  return { first_name: '', last_name: toProperCase(cleaned), suffix };
};

const toProperCase = (str: string): string => {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bMc(\w)/g, (_, c) => `Mc${c.toUpperCase()}`)
    .replace(/\bO'(\w)/g, (_, c) => `O'${c.toUpperCase()}`);
};

const parseAmount = (raw: string): number | null => {
  if (!raw) return null;
  const cleaned = raw.replace(/[$,]/g, '').replace(/\./g, (m, offset, str) => {
    // Keep only the last period as decimal
    const lastDot = str.lastIndexOf('.');
    return offset === lastDot ? '.' : '';
  });
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
};

export function BuncombeCountyImport({ onImportComplete }: Props) {
  const [textData, setTextData] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [preview, setPreview] = useState<ParsedRecord[]>([]);
  const { toast } = useToast();

  const parseData = (text: string): ParsedRecord[] => {
    const lines = text.trim().split('\n').filter(l => l.trim().length > 0);
    const records: ParsedRecord[] = [];
    const seen = new Set<string>();

    for (const line of lines) {
      // Skip header line
      if (line.includes('Recording Date') && line.includes('Document Type')) continue;

      const parts = line.split('\t');
      if (parts.length < 5) continue;

      const county = (parts[0] || '').trim();
      const dateRaw = (parts[1] || '').trim();
      const docType = (parts[2] || '').trim();
      const parcelId = (parts[3] || '').trim().replace(/,\s*$/, '');
      const nameRaw = (parts[4] || '').trim();
      const street = (parts[5] || '').trim();
      const city = (parts[6] || '').trim();
      const state = (parts[7] || '').trim();
      const zip = (parts[8] || '').trim();
      const amountRaw = (parts[9] || '').trim();

      if (!nameRaw) continue;

      // Parse date to YYYY-MM-DD
      const dateParts = dateRaw.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      const recorded_date = dateParts
        ? `${dateParts[3]}-${dateParts[1].padStart(2, '0')}-${dateParts[2].padStart(2, '0')}`
        : '';

      const { first_name, last_name, suffix } = parseName(nameRaw);

      // Generate seller_id from name + date for dedup
      const seller_id = `BUNCOMBE-${last_name.toUpperCase()}-${first_name.toUpperCase()}-${recorded_date}`.replace(/\s+/g, '-');

      if (seen.has(seller_id)) continue;
      seen.add(seller_id);

      // Clean address - remove name echo (e.g. "Stephanie Johnson 38 Deanwood Circle")
      let cleanStreet = street;
      if (first_name && last_name) {
        const namePattern = new RegExp(`^${first_name}\\s+${last_name}\\s+`, 'i');
        cleanStreet = cleanStreet.replace(namePattern, '');
      }

      records.push({
        seller_id,
        county: county || 'Buncombe',
        origin_state: 'NC',
        recorded_date,
        document_type: docType,
        pid: parcelId,
        first_name,
        last_name,
        suffix,
        street_address: cleanStreet,
        city: city || '',
        state: state.trim() || '',
        zip_code: zip || '',
        consideration: parseAmount(amountRaw),
      });
    }

    return records;
  };

  const handlePreview = () => {
    const records = parseData(textData);
    setPreview(records);
    toast({
      title: `Parsed ${records.length} records`,
      description: `${records.filter(r => r.street_address).length} have addresses, ${records.filter(r => !r.street_address).length} missing addresses`,
    });
  };

  const handleImport = async () => {
    if (preview.length === 0) return;
    setIsProcessing(true);

    try {
      const batchSize = 500;
      let imported = 0;

      for (let i = 0; i < preview.length; i += batchSize) {
        const batch = preview.slice(i, i + batchSize).map(r => ({
          seller_id: r.seller_id,
          county: r.county,
          origin_state: r.origin_state,
          recorded_date: r.recorded_date || null,
          document_type: r.document_type,
          pid: r.pid || null,
          first_name: r.first_name,
          last_name: r.last_name,
          suffix: r.suffix || null,
          street_address: r.street_address || null,
          city: r.city || null,
          state: r.state || null,
          zip_code: r.zip_code || null,
          consideration: r.consideration,
          mailing_status: 'active' as const,
        }));

        const { error } = await supabase
          .from('mailing_records')
          .upsert(batch, { onConflict: 'seller_id' });

        if (error) throw error;
        imported += batch.length;
      }

      toast({ title: "Import complete", description: `${imported} Buncombe County records imported` });
      setPreview([]);
      setTextData("");
      onImportComplete();
    } catch (err: any) {
      toast({ title: "Import failed", description: err.message, variant: "destructive" });
    } finally {
      setIsProcessing(false);
    }
  };

  const withAddress = preview.filter(r => r.street_address);
  const noAddress = preview.filter(r => !r.street_address);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Buncombe County Import
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Paste tab-separated Buncombe County deed of trust data. Columns: County, Recording Date, Document Type, Parcel ID, Lender/Name, Address, City, State, Zip, Amount.
        </p>

        <Textarea
          value={textData}
          onChange={e => setTextData(e.target.value)}
          placeholder="Paste Buncombe County data here..."
          rows={10}
          className="font-mono text-xs"
        />

        <div className="flex gap-2">
          <Button onClick={handlePreview} disabled={!textData.trim()}>
            <Upload className="h-4 w-4 mr-2" /> Preview
          </Button>
          {preview.length > 0 && (
            <Button onClick={handleImport} disabled={isProcessing}>
              {isProcessing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Import {preview.length} Records
            </Button>
          )}
        </div>

        {preview.length > 0 && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Badge variant="default">{preview.length} total</Badge>
              <Badge variant="secondary">{withAddress.length} with address</Badge>
              <Badge variant="outline">{noAddress.length} no address</Badge>
            </div>

            <div className="border rounded-md overflow-auto max-h-96">
              <table className="w-full text-xs">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Address</th>
                    <th className="p-2 text-left">City, State Zip</th>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.slice(0, 50).map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2">{r.first_name} {r.last_name} {r.suffix}</td>
                      <td className="p-2">{r.street_address || <span className="text-muted-foreground italic">none</span>}</td>
                      <td className="p-2">{[r.city, r.state].filter(Boolean).join(', ')} {r.zip_code}</td>
                      <td className="p-2">{r.recorded_date}</td>
                      <td className="p-2 text-right">{r.consideration ? `$${r.consideration.toLocaleString()}` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.length > 50 && (
                <p className="p-2 text-center text-muted-foreground text-xs">Showing 50 of {preview.length}...</p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
